import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';

/**
 * Explore page: single hub for Discover options — Businesses, Events, Ads, Properties, Turfs, Points.
 * No API calls; presentational only. Matches main theme (rounded-2xl, shadow-sm, primary).
 */
const EXPLORE_OPTIONS = [
  {
    id: 'institutes',
    title: 'Institutes',
    description: 'Find schools, colleges, universities, and training centers in your community.',
    path: '/institutes',
    label: 'Browse Institutes',
  },
  {
    id: 'businesses',
    title: 'Businesses',
    description: 'Find local shops, services, restaurants, and companies in your area.',
    path: '/businesses',
    label: 'Browse Businesses',
  },
  {
    id: 'events',
    title: 'Events',
    description: 'Discover upcoming events, meetups, and community gatherings.',
    path: '/events',
    label: 'Find Events',
  },
  {
    id: 'ads',
    title: 'Ads',
    description: 'Browse and post classified ads within your community.',
    path: '/ads',
    label: 'Browse Ads',
  },
  {
    id: 'properties',
    title: 'Properties',
    description: 'Explore residential and commercial properties for rent or sale.',
    path: '/properties',
    label: 'View Properties',
  },
  {
    id: 'turfs',
    title: 'Turfs',
    description: 'Book sports grounds, courts, and recreational spaces.',
    path: '/turfs',
    label: 'Explore Turfs',
  },
  {
    id: 'points',
    title: 'Points',
    description: 'Travel, visas, and points of interest across Pakistan.',
    path: '/points',
    label: 'Discover Points',
  },
];

const ExplorePage = () => {
  return (
    <PageLayout title="Explore - PakPoints">
      <Helmet>
        <title>Explore - PakPoints</title>
        <meta name="description" content="Discover businesses, events, ads, properties, turfs, and points across Pakistan." />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero strip: consistent with home theme */}
        <section
          className="relative py-12 sm:py-16 overflow-hidden"
          aria-label="Explore overview"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50" aria-hidden="true" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-100/30 via-transparent to-transparent" aria-hidden="true" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Explore
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Businesses, institutes, events, ads, properties, turfs, and points — everything you need in one place.
            </p>
          </div>
        </section>

        {/* Option cards */}
        <section
          className="pb-16 sm:pb-20"
          aria-labelledby="explore-options-heading"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="explore-options-heading" className="sr-only">
              Explore by category
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {EXPLORE_OPTIONS.map((option) => (
                <Link
                  key={option.id}
                  to={option.path}
                  className="group rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  <div
                    className="w-14 h-14 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center text-2xl font-bold group-hover:bg-primary-200 transition-colors"
                    aria-hidden="true"
                  >
                    {option.title.charAt(0)}
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {option.title}
                  </h3>
                  <p className="mt-2 text-gray-600 flex-1">
                    {option.description}
                  </p>
                  <span className="mt-6 inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700">
                    {option.label}
                    <svg className="ml-1.5 w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default ExplorePage;
